import React, { useState, useEffect, useRef } from 'react';
import { Download, RotateCcw, Copy, Check, Star } from 'lucide-react';

// ─── Download helper ─────────────────────────────────────────────────────────

function generateDownload(results, brief) {
  const line = '─'.repeat(60);
  const { targets } = results.scout;
  const { target_name, subject, body } = results.outreach;
  const { battles } = results.objection;

  return [
    '  RUNGTM — GTM PACKAGE',
    `  ${new Date().toLocaleString('en-IN')}`,
    `  "${brief}"`,
    '',
    line,
    '  SCOUT — TARGET COMPANIES',
    line,
    '',
    ...targets.flatMap((t, i) => [
      `  ${i + 1}. ${t.name}  [${'★'.repeat(t.score)}${'☆'.repeat(5 - t.score)}]`,
      `     Why: ${t.why}`,
      `     Insight: ${t.insight}`,
      '',
    ]),
    line,
    '  OUTREACH — COLD EMAIL',
    line,
    '',
    `  TO: ${target_name}`,
    `  SUBJECT: ${subject}`,
    '',
    ...body.split('\n').map((l) => `  ${l}`),
    '',
    line,
    '  OBJECTION BATTLE CARD',
    line,
    '',
    ...battles.flatMap((b, i) => [
      `  ${i + 1}. "${b.objection}"`,
      `     → ${b.response}`,
      '',
    ]),
    line,
    '  RunGTM',
    line,
  ].join('\n');
}

function triggerDownload(results, brief) {
  const content = generateDownload(results, brief);
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'gtm-package.txt';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Marketers Orb — red square, 6px radius, bold white megaphone icon
function MarketersOrb({ size = 28 }) {
  return (
    <div
      className="inline-flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, background: '#e60000', borderRadius: '6px' }}
      aria-label="Marketers"
    >
      <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 20 16" fill="none">
        {/* Mouthpiece box */}
        <rect x="0" y="4" width="4" height="8" rx="1" fill="white" />
        {/* Horn — cone flaring right */}
        <path d="M4 3L19 0V16L4 13V3Z" fill="white" />
      </svg>
    </div>
  );
}

// ─── Star Rating ─────────────────────────────────────────────────────────────

function StarRating({ score }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={11}
          className={i < score ? 'text-primary fill-primary' : 'text-mute'}
        />
      ))}
    </div>
  );
}

// ─── Scout Panel ─────────────────────────────────────────────────────────────

function TargetCard({ target, index }) {
  const [visible, setVisible] = useState(false);
  const hasUrl = Boolean(target.url);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 200 + index * 300);
    return () => clearTimeout(t);
  }, [index]);

  const handleClick = () => {
    if (hasUrl) window.open(target.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      data-testid={`scout-target-card-${index}`}
      onClick={handleClick}
      className={`mb-3 p-5 bg-canvas transition-all duration-200 ${hasUrl ? 'cursor-pointer' : 'cursor-default'}`}
      style={{
        borderRadius: '6px',
        border: '1.5px solid #e8e8e8',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-16px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease, border-color 0.15s ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#e60000')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e8e8e8')}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-ink font-bold text-sm leading-snug">{target.name}</span>
          {hasUrl && (
            <svg
              width="11" height="11" viewBox="0 0 12 12" fill="none"
              className="flex-shrink-0 text-primary opacity-60"
              style={{ marginTop: '1px' }}
            >
              <path d="M2 2h4V1H1v10h10V7h-1v4H2V2zm5-1v1h2.293L4.146 7.146l.708.708L10 2.707V5h1V1H7z" fill="#e60000"/>
            </svg>
          )}
        </div>
        <div className="flex-shrink-0 mt-0.5">
          <StarRating score={target.score} />
        </div>
      </div>
      <p className="text-body-text text-xs leading-relaxed mb-3">{target.why}</p>
      <div
        className="pl-3"
        style={{ borderLeft: '2px solid #e60000' }}
      >
        <p className="text-ink text-xs leading-relaxed italic">{target.insight}</p>
      </div>
    </div>
  );
}

function ScoutPanel({ targets }) {
  return (
    <div
      className="overflow-y-auto p-5"
      style={{ borderRight: '1px solid #e8e8e8' }}
      data-testid="scout-panel"
    >
      <div className="flex items-center gap-2 mb-5">
        <p
          className="text-primary font-extrabold tracking-widest"
          style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px' }}
        >
          Scout
        </p>
        <span
          className="ml-auto text-xs font-semibold text-body-text px-2 py-0.5"
          style={{ background: '#f2f2f2', borderRadius: '32px' }}
        >
          {targets.length} targets
        </span>
      </div>

      {targets.map((target, i) => (
        <TargetCard key={i} target={target} index={i} />
      ))}
    </div>
  );
}

// ─── Outreach Panel ───────────────────────────────────────────────────────────

function ContactChip({ contact, subject, body }) {
  const hasEmail = Boolean(contact.email);

  const handleClick = () => {
    if (!hasEmail) return;
    const mailto = `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  return (
    <div
      data-testid={`contact-chip-${contact.company.replace(/\s+/g, '-').toLowerCase()}`}
      onClick={handleClick}
      className="flex items-center gap-3 mb-2 px-3 py-2.5 transition-all duration-150"
      style={{
        background: '#f2f2f2',
        borderRadius: '6px',
        border: '1.5px solid transparent',
        cursor: hasEmail ? 'pointer' : 'default',
      }}
      onMouseEnter={(e) => {
        if (hasEmail) e.currentTarget.style.borderColor = '#e60000';
      }}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'transparent')}
    >
      {/* Company initial avatar */}
      <div
        className="w-6 h-6 flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
        style={{ background: '#25282b', borderRadius: '4px' }}
      >
        {contact.company[0].toUpperCase()}
      </div>

      {/* Company name + email */}
      <div className="flex-1 min-w-0">
        <p className="text-ink text-xs font-bold leading-none mb-0.5">{contact.company}</p>
        {hasEmail ? (
          <p className="text-body-text text-xs truncate leading-none">{contact.email}</p>
        ) : (
          <p className="text-mute text-xs leading-none italic">Not found publicly</p>
        )}
      </div>

      {/* Mail icon */}
      {hasEmail && (
        <div className="flex-shrink-0">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-mute">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>
      )}
    </div>
  );
}

function OutreachPanel({ email, contacts }) {
  const [displayedText, setDisplayedText] = useState('');
  const [streamDone, setStreamDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const wordIndexRef = useRef(0);

  useEffect(() => {
    const words = email.body.split(' ');
    const startTimer = setTimeout(() => {
      const interval = setInterval(() => {
        const i = wordIndexRef.current;
        if (i < words.length) {
          wordIndexRef.current = i + 1;
          setDisplayedText(words.slice(0, i + 1).join(' '));
        } else {
          clearInterval(interval);
          setStreamDone(true);
        }
      }, 60);
      return () => clearInterval(interval);
    }, 400);
    return () => clearTimeout(startTimer);
  }, [email.body]);

  const handleCopy = () => {
    const text = `Subject: ${email.subject}\n\n${email.body}`;
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="flex flex-col overflow-y-auto p-5"
      style={{ borderRight: '1px solid #e8e8e8' }}
      data-testid="outreach-panel"
    >
      <div className="flex items-center gap-2 mb-5">
        <p
          className="text-primary font-extrabold tracking-widest"
          style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px' }}
        >
          Outreach
        </p>
        <span
          className="ml-auto text-xs font-semibold text-body-text px-2 py-0.5"
          style={{ background: '#f2f2f2', borderRadius: '32px' }}
        >
          Cold email
        </span>
      </div>

      {/* Email card */}
      <div
        className="flex-1 flex flex-col bg-canvas"
        style={{
          border: '1.5px solid #25282b',
          borderRadius: '6px',
          overflow: 'hidden',
        }}
      >
        {/* Email header band */}
        <div
          className="px-4 py-3"
          style={{
            background: '#25282b',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <p className="text-white/60 text-xs mb-0.5">
            To: <span className="text-white font-medium">{email.target_name}</span>
          </p>
          <p className="text-white/60 text-xs">
            Subject: <span className="text-white font-semibold">{email.subject}</span>
          </p>
        </div>

        {/* Email body */}
        <div className="flex-1 p-4">
          <pre
            data-testid="email-body"
            className="text-ink text-sm whitespace-pre-wrap leading-relaxed min-h-[100px]"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {displayedText}
            {!streamDone && (
              <span className="cursor-blink text-primary ml-px">|</span>
            )}
          </pre>
        </div>
      </div>

      {/* Copy button — outline-dark pill */}
      <button
        data-testid="copy-email-button"
        onClick={handleCopy}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 font-normal transition-all duration-150"
        style={{
          background: '#ffffff',
          border: '1px solid #25282b',
          borderRadius: '60px',
          padding: '10px 20px',
          fontSize: '14px',
          color: copied ? '#e60000' : '#25282b',
          borderColor: copied ? '#e60000' : '#25282b',
        }}
        onMouseEnter={(e) => {
          if (!copied) e.currentTarget.style.background = '#f2f2f2';
        }}
        onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
      >
        {copied ? (
          <>
            <Check size={13} strokeWidth={2.5} />
            Copied
          </>
        ) : (
          <>
            <Copy size={13} />
            Copy email
          </>
        )}
      </button>

      {/* ── Send To — contact chips ──────────────────────────── */}
      {contacts && contacts.length > 0 && (
        <div className="mt-5">
          <div
            className="mb-3"
            style={{ borderTop: '1px solid #e8e8e8', paddingTop: '16px' }}
          >
            <p
              className="text-primary font-extrabold tracking-widest mb-3"
              style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px' }}
            >
              Send to
            </p>
            {contacts.map((c, i) => (
              <ContactChip key={i} contact={c} subject={email.subject} body={email.body} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Objection Panel ──────────────────────────────────────────────────────────

function ObjectionCard({ battle, index }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 400 + index * 300);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div
      data-testid={`objection-card-${index}`}
      className="mb-3 bg-canvas transition-all duration-200 cursor-default"
      style={{
        borderRadius: '6px',
        border: '1.5px solid #e8e8e8',
        overflow: 'hidden',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease, border-color 0.15s ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#e60000')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e8e8e8')}
    >
      {/* Objection — red band */}
      <div
        className="px-4 py-3"
        style={{ background: '#fff5f5', borderBottom: '1px solid #fde0e0' }}
      >
        <p
          className="text-xs leading-relaxed font-medium"
          style={{ color: '#cc0000' }}
        >
          &ldquo;{battle.objection}&rdquo;
        </p>
      </div>
      {/* Response */}
      <div className="px-4 py-3">
        <p className="text-ink text-xs leading-relaxed">
          <span className="text-primary font-bold">→ </span>
          {battle.response}
        </p>
      </div>
    </div>
  );
}

function ObjectionPanel({ battles }) {
  return (
    <div className="overflow-y-auto p-5" data-testid="objection-panel">
      <div className="flex items-center gap-2 mb-5">
        <p
          className="text-primary font-extrabold tracking-widest"
          style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px' }}
        >
          Objections
        </p>
        <span
          className="ml-auto text-xs font-semibold text-body-text px-2 py-0.5"
          style={{ background: '#f2f2f2', borderRadius: '32px' }}
        >
          Battle card
        </span>
      </div>
      {battles.map((battle, i) => (
        <ObjectionCard key={i} battle={battle} index={i} />
      ))}
    </div>
  );
}

// ─── GTM Package Bar (red hero band) ─────────────────────────────────────────

function GTMPackageBar({ show, results, brief, onReset }) {
  return (
    <div
      data-testid="gtm-package-bar"
      className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4"
      style={{
        background: '#e60000',
        transform: show ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <div>
        <p className="text-white font-extrabold uppercase tracking-wide" style={{ fontSize: '14px', letterSpacing: '1px' }}>
          Your GTM package is ready. Go close.
        </p>
        <p className="text-white/70 text-xs mt-0.5">
          {results.scout.targets.length} targets · 1 email · {results.objection.battles.length} rebuttals
        </p>
      </div>

      <div className="flex items-center gap-3">
        {/* White pill button on red */}
        <button
          data-testid="download-gtm-button"
          onClick={() => triggerDownload(results, brief)}
          className="inline-flex items-center gap-2 font-medium transition-colors duration-150"
          style={{
            background: '#ffffff',
            border: '1px solid #ffffff',
            borderRadius: '60px',
            padding: '10px 22px',
            fontSize: '14px',
            color: '#e60000',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#f2f2f2')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
        >
          <Download size={13} strokeWidth={2.5} />
          Download all
        </button>

        {/* Outline white ghost */}
        <button
          data-testid="run-another-button"
          onClick={onReset}
          className="inline-flex items-center gap-2 font-medium transition-colors duration-150"
          style={{
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: '60px',
            padding: '10px 22px',
            fontSize: '14px',
            color: '#ffffff',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#ffffff')}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)')}
        >
          <RotateCcw size={12} />
          Run another
        </button>
      </div>
    </div>
  );
}

// ─── Results Screen ───────────────────────────────────────────────────────────

export default function ResultsScreen({ results, brief, onReset }) {
  const [showBar, setShowBar] = useState(false);

  // Build contact list from scout targets (static emails from fallback)
  const contactEmails = results.scout.targets.map((t) => ({
    company: t.name,
    email: t.email || null,
  }));

  useEffect(() => {
    const t = setTimeout(() => setShowBar(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const briefPreview = brief.length > 90 ? brief.substring(0, 90) + '…' : brief;

  return (
    <div className="min-h-screen bg-canvas flex flex-col pb-[68px]">

      {/* Header — ink band */}
      <header
        className="sticky top-0 z-30 flex items-center justify-between px-6 h-14"
        style={{ background: '#25282b' }}
        data-testid="results-header"
      >
        <div className="flex items-center gap-2">
          <MarketersOrb size={26} />
          <span className="text-white font-bold text-base tracking-tight">Marketers</span>
        </div>

        <span
          className="text-xs font-bold uppercase tracking-widest px-3 py-1"
          style={{
            background: '#e60000',
            color: '#ffffff',
            borderRadius: '32px',
            letterSpacing: '1px',
          }}
        >
          Package Ready
        </span>

        <button
          data-testid="header-download-button"
          onClick={() => triggerDownload(results, brief)}
          className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-medium transition-colors duration-150"
        >
          <Download size={12} />
          Download
        </button>
      </header>

      {/* Brief strip — canvas-soft */}
      <div
        className="px-6 py-2.5"
        style={{ background: '#f2f2f2', borderBottom: '1px solid #e8e8e8' }}
      >
        <p className="text-body-text text-xs">
          <span className="font-bold uppercase tracking-wider" style={{ fontSize: '11px', letterSpacing: '1px' }}>Brief</span>
          <span className="mx-2 text-mute">—</span>
          <span>"{briefPreview}"</span>
        </p>
      </div>

      {/* Three columns */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 min-h-0 bg-canvas">
        <ScoutPanel targets={results.scout.targets} />
        <OutreachPanel email={results.outreach} contacts={contactEmails} />
        <ObjectionPanel battles={results.objection.battles} />
      </div>

      <GTMPackageBar show={showBar} results={results} brief={brief} onReset={onReset} />
    </div>
  );
}
